/**
 * Loop Studio brand marks, straight from the brand book (v0.6).
 * The loop = the logomark (one paper strip folded into a loop, the seam = the splice).
 * The dart = "the wrap" (the finished cut, flying). Both are pure SVG, server-safe.
 */

/**
 * The loop, built from its 8 folded-paper facets so each can move on its own.
 * At rest they tile the octagon exactly; on hover they scatter outward + tumble,
 * then spring back into place (see .ls-loop in globals.css). No JS — pure SVG+CSS.
 */
const LOOP_FACETS: { p: string; f: string; tx: string; ty: string; r: string }[] = [
  { p: "48,6 78,18 62,34 48,28", f: "#cfff05", tx: "0.9%", ty: "-2.2%", r: "-3deg" },
  { p: "78,18 90,48 68,48 62,34", f: "#bced04", tx: "2.2%", ty: "-0.9%", r: "3deg" },
  { p: "90,48 78,78 62,62 68,48", f: "#9cc400", tx: "2.2%", ty: "0.9%", r: "-3deg" },
  { p: "78,78 48,90 48,68 62,62", f: "#7ba000", tx: "0.9%", ty: "2.2%", r: "3deg" },
  { p: "48,90 18,78 34,62 48,68", f: "#9cc400", tx: "-0.9%", ty: "2.2%", r: "-3deg" },
  { p: "18,78 6,48 28,48 34,62", f: "#bced04", tx: "-2.2%", ty: "0.9%", r: "3deg" },
  { p: "6,48 18,18 34,34 28,48", f: "#cfff05", tx: "-2.2%", ty: "-0.9%", r: "-3deg" },
  { p: "18,18 48,6 48,28 34,34", f: "#d6ff33", tx: "-0.9%", ty: "-2.2%", r: "3deg" },
];

// The same eight facets, rearranged into a left-pointing loop arrow. The
// negative space remains open, so the hover state still reads as the Loop
// Studio mark instead of swapping to an unrelated icon.
const LOOP_ARROW_FACETS = [
  "42,30 88,30 78,40 52,40",
  "88,30 88,48 78,48 78,40",
  "88,48 88,66 78,56 78,48",
  "88,66 42,66 52,56 78,56",
  "42,66 42,82 52,68 52,56",
  "42,82 8,48 31,48 52,68",
  "8,48 42,14 52,28 31,48",
  "42,14 42,30 52,40 52,28",
];

// The same eight facets folded into a house, for the logo everywhere OUTSIDE the
// editor — "this takes you home" rather than "this takes you back one level".
// Read clockwise from the ridge, matching the loop's own facet order, so each
// facet travels the short way to its new position and keeps its colour band.
// The body stays hollow and the doorway stays open, so the silhouette still
// carries the loop's negative space instead of becoming a solid pictogram.
const LOOP_HOUSE_FACETS = [
  "48,10 67,29 58,37 48,26", // roof, ridge to mid-right
  "67,29 86,48 68,48 58,37", // roof, mid-right to eave
  "80,48 80,86 66,86 66,48", // right wall
  "66,74 66,86 54,86 54,74", // right door jamb
  "42,74 42,86 30,86 30,74", // left door jamb
  "30,48 30,86 16,86 16,48", // left wall
  "29,29 10,48 28,48 38,37", // roof, eave to mid-left
  "48,10 29,29 38,37 48,26", // roof, mid-left to ridge
];

/* ── the loader set: the same eight facets, folded into what the studio does ──
   Every target below is EIGHT strings of FOUR points, in the loop's own facet
   order. That is the whole contract: matched vertex counts are what let the CSS
   `d` property interpolate instead of snapping, and keeping the order means each
   facet travels the short way and holds its colour band, so the mark reads as
   one object folding rather than four unrelated icons swapping.

   The order is also the argument, which is why these three and not any three:
   footage goes in, sound is read, something is made of it.
   ────────────────────────────────────────────────────────────────────────── */

/** A contact sheet — 4x2 cells. Facets keep their side: left facets, left cells. */
const LOOP_FRAMES_FACETS = [
  "30,28 48,28 48,46 30,46",
  "52,28 70,28 70,46 52,46",
  "74,28 92,28 92,46 74,46",
  "74,52 92,52 92,70 74,70",
  "52,52 70,52 70,70 52,70",
  "30,52 48,52 48,70 30,70",
  "8,52 26,52 26,70 8,70",
  "8,28 26,28 26,46 8,46",
];

/** A waveform — eight bars, left to right, tallest through the middle. */
const LOOP_WAVE_FACETS = [
  "8,37 15,37 15,59 8,59",
  "19,28 26,28 26,68 19,68",
  "30,19 37,19 37,77 30,77",
  "41,10 48,10 48,86 41,86",
  "52,13 59,13 59,83 52,83",
  "63,25 70,25 70,71 63,71",
  "74,32 81,32 81,64 74,64",
  "85,39 92,39 92,57 85,57",
];

/** A sparkle — eight rays, long on the cardinals, short on the diagonals. */
const LOOP_SPARKLE_FACETS = [
  "43.6,33.7 43.9,2.2 52.1,2.2 52.4,33.7",
  "55,34.8 67.9,24.2 71.8,28.1 61.2,41",
  "62.3,43.6 93.8,43.9 93.8,52.1 62.3,52.4",
  "61.2,55 71.8,67.9 67.9,71.8 55,61.2",
  "52.4,62.3 52.1,93.8 43.9,93.8 43.6,62.3",
  "41,61.2 28.1,71.8 24.2,67.9 34.8,55",
  "33.7,52.4 2.2,52.1 2.2,43.9 33.7,43.6",
  "34.8,41 24.2,28.1 28.1,24.2 41,34.8",
];

function pointsToPath(points: string) {
  const pairs = points.split(" ").map((pair) => pair.replace(",", " "));
  return `M ${pairs.join(" L ")} Z`;
}

/**
 * The loader's cycle, precomputed as paths so a client component can consume it
 * without importing the geometry or duplicating pointsToPath. Starts and ends on
 * the loop, because the brand is the thing that has to be legible if the screen
 * is only up for half a second.
 *
 * Exported from here rather than defined in the loader so there is exactly one
 * place the mark's facets are described. brand.tsx stays free of hooks — it is
 * imported by server components and must remain server-safe.
 */
export const LOOP_LOADER_STEPS: { name: string; paths: string[] }[] = [
  { name: "loop", paths: LOOP_FACETS.map((facet) => pointsToPath(facet.p)) },
  { name: "frames", paths: LOOP_FRAMES_FACETS.map(pointsToPath) },
  { name: "wave", paths: LOOP_WAVE_FACETS.map(pointsToPath) },
  { name: "sparkle", paths: LOOP_SPARKLE_FACETS.map(pointsToPath) },
];

/** The eight colour bands, so the loader keeps the mark's fold shading. */
export const LOOP_FILLS: string[] = LOOP_FACETS.map((facet) => facet.f);

/** Guards the contract above. A mismatch snaps instead of morphing, and the
 *  only place you would notice is a loading screen that is gone in a second. */
for (const [name, target] of Object.entries({ LOOP_FRAMES_FACETS, LOOP_WAVE_FACETS, LOOP_SPARKLE_FACETS })) {
  if (target.length !== LOOP_FACETS.length) throw new Error(`${name}: needs ${LOOP_FACETS.length} facets, has ${target.length}`);
  const bad = target.findIndex((points) => points.trim().split(/\s+/).length !== 4);
  if (bad !== -1) throw new Error(`${name}: facet ${bad} does not have four points, so d cannot interpolate`);
}

export function Loopmark({ size = 28, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" className={`ls-loop ${className}`} aria-hidden="true">
      {LOOP_FACETS.map((s, i) => (
        <polygon
          key={i}
          points={s.p}
          fill={s.f}
          stroke="#0f121a"
          strokeWidth="0.6"
          strokeOpacity="0.22"
          strokeLinejoin="round"
          className="ls-seg"
          style={{ ["--tx" as string]: s.tx, ["--ty" as string]: s.ty, ["--rot" as string]: s.r, transitionDelay: `${i * 16}ms` }}
        />
      ))}
    </svg>
  );
}

/**
 * The loop folding into another eight-facet shape on hover. One renderer for
 * every target: the facets, their colours and the stagger are identical, so the
 * arrow and the house feel like the same gesture rather than two effects.
 */
function LoopMorph({ to, size, className }: { to: readonly string[]; size: number; className: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" className={`ls-back-morph ${className}`} aria-hidden="true">
      {LOOP_FACETS.map((facet, index) => {
        const loopPath = pointsToPath(facet.p);
        const morphPath = pointsToPath(to[index]);
        return (
          <path
            key={index}
            d={loopPath}
            fill={facet.f}
            stroke="#0f121a"
            strokeWidth="0.6"
            strokeOpacity="0.22"
            strokeLinejoin="round"
            className="ls-back-morph-seg"
            style={{
              ["--loop-path" as string]: `path("${loopPath}")`,
              ["--morph-path" as string]: `path("${morphPath}")`,
              transitionDelay: `${index * 12}ms`,
            }}
          />
        );
      })}
    </svg>
  );
}

/**
 * The mark as a flat single-colour silhouette, for laying into a field as a
 * watermark. Deliberately not `Loopmark`: at 3% the eight lime tones collapse
 * into mud, and the facet seams that give the logo its fold read as noise.
 * One fill, one shape.
 */
export function LoopSilhouette({ className = "", fill = "#0f121a" }: { className?: string; fill?: string }) {
  return (
    <svg viewBox="0 0 96 96" className={className} aria-hidden="true">
      {LOOP_FACETS.map((facet, index) => (
        <polygon key={index} points={facet.p} fill={fill} />
      ))}
    </svg>
  );
}

/** The loop folding into a back arrow — used inside the editor. */
export function LoopArrowMorph({ size = 28, className = "" }: { size?: number; className?: string }) {
  return <LoopMorph to={LOOP_ARROW_FACETS} size={size} className={className} />;
}

/** The loop folding into the sparkle: the mark of the AI director. */
export function LoopSparkleMorph({ size = 28, className = "" }: { size?: number; className?: string }) {
  return <LoopMorph to={LOOP_SPARKLE_FACETS} size={size} className={className} />;
}

/** The mark held in its sparkle form, with the loop's fold shading. */
export function LoopSparkle({ size = 28, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" className={className} aria-hidden="true">
      {LOOP_SPARKLE_FACETS.map((points, index) => (
        <polygon key={index} points={points} fill={LOOP_FACETS[index].f} stroke="#0f121a" strokeWidth="0.6" strokeOpacity="0.22" strokeLinejoin="round" />
      ))}
    </svg>
  );
}

/** The loop folding into a house — used everywhere else, meaning "back to the start". */
export function LoopHouseMorph({ size = 28, className = "" }: { size?: number; className?: string }) {
  return <LoopMorph to={LOOP_HOUSE_FACETS} size={size} className={className} />;
}

export function PaperDart({ width = 96, className }: { width?: number; className?: string }) {
  const height = Math.round((width * 128) / 168);
  return (
    <svg width={width} height={height} viewBox="0 0 168 128" className={className} aria-hidden="true">
      <polygon points="150,60 22,16 66,62" fill="#CFFF05" />
      <polygon points="150,60 66,62 22,104" fill="#bced04" />
      <polygon points="22,16 66,62 22,104" fill="#8fb300" />
      <polygon points="66,62 150,60 62,74" fill="#9cc400" />
      <line x1="22" y1="16" x2="66" y2="62" stroke="#f8f6f0" strokeWidth="1.4" />
      <line x1="150" y1="60" x2="66" y2="62" stroke="#f8f6f0" strokeWidth="1.2" opacity="0.7" />
    </svg>
  );
}

/** The wordmark lockup — Loop (700) carries the weight, Studio (300) whispers. */
/**
 * The wordmark as the landing page writes it: one word, light weight, wide
 * tracking, uppercase, next to the mark. The studio, sign-in, onboarding and
 * the public nav used a two-tone "Loop Studio" until 7 September 2026; the
 * owner wants one wordmark everywhere.
 */
export function Wordmark({ className = "", size = 14 }: { className?: string; size?: number }) {
  return (
    <span
      className={`text-silver ${className}`}
      style={{ fontSize: size, fontWeight: 300, letterSpacing: "0.26em", textTransform: "uppercase", lineHeight: 1, whiteSpace: "nowrap" }}
    >
      LoopStudio
    </span>
  );
}
